import React, { useMemo } from 'react';
import { AiOutlineClockCircle, AiOutlineWarning, AiOutlineExclamationCircle } from 'react-icons/ai';
import moment from 'moment-timezone';

const CUTOFF_HOUR = 14; // 14:00 horário limite

const warningStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '1rem',
    marginBottom: '0.5rem',
  },
  box: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.875rem 1rem',
    borderRadius: '10px',
    fontSize: '0.85rem',
    lineHeight: '1.45',
  },
  info: {
    backgroundColor: '#EFF6FF',
    border: '1px solid #BFDBFE',
    color: '#1E40AF',
  },
  warn: {
    backgroundColor: '#FFFBEB',
    border: '1px solid #FDE68A',
    color: '#92400E',
  },
  danger: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    color: '#991B1B',
  },
  icon: {
    flexShrink: 0,
    marginTop: '2px',
  },
  bold: {
    fontWeight: 600,
  },
};

/**
 * Verifica se o pagamento será processado no mesmo dia
 * Retorna { willProcessToday, reason }
 */
export function getPaymentProcessingInfo() {
  const now = moment().tz('America/Sao_Paulo');
  const hour = now.hour();
  const dayOfWeek = now.day(); // 0 = domingo, 6 = sábado

  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isAfterCutoff = hour >= CUTOFF_HOUR;

  if (isWeekend) {
    return {
      willProcessToday: false,
      reason: 'weekend',
      message: 'Hoje é final de semana. O pagamento será processado no próximo dia útil.',
    };
  }

  if (isAfterCutoff) {
    return {
      willProcessToday: false,
      reason: 'after-cutoff',
      message: `São mais de ${CUTOFF_HOUR}:00. O pagamento será processado no próximo dia útil.`,
    };
  }

  return {
    willProcessToday: true,
    reason: null,
    message: null,
  };
}

/**
 * Verifica se o boleto pode ter problemas por ser processado no dia seguinte
 * (vencimento no dia atual + processamento só no próximo dia útil)
 */
function checkDueDateRisk(dueDate, willProcessToday) {
  if (willProcessToday || !dueDate) return null;

  const now = moment().tz('America/Sao_Paulo');
  const due = moment(dueDate).tz('America/Sao_Paulo');

  // Boleto vence hoje mas só será processado amanhã
  if (due.isSame(now, 'day')) {
    return {
      level: 'danger',
      message:
        'Este boleto vence hoje, porém o pagamento só será processado no próximo dia útil. ' +
        'Caso o boleto não aceite pagamento após o vencimento, ele poderá ser cancelado ou poderão incidir juros e multa.',
    };
  }

  // Boleto já está vencido
  if (due.isBefore(now, 'day')) {
    return {
      level: 'danger',
      message:
        'Este boleto já está vencido. Caso o boleto não aceite pagamento após o vencimento, ele poderá ser cancelado ou poderão incidir juros e multa.',
    };
  }

  return null;
}

/**
 * Componente de avisos de processamento de pagamento de boleto
 * Exibir ANTES de confirmar o pagamento (após consultar código de barras)
 */
export const PaymentWarnings = ({ dueDate }) => {
  const processingInfo = useMemo(() => getPaymentProcessingInfo(), []);
  const dueDateRisk = useMemo(
    () => checkDueDateRisk(dueDate, processingInfo.willProcessToday),
    [dueDate, processingInfo.willProcessToday]
  );

  return (
    <div style={warningStyles.container}>
      {/* Aviso geral - sempre exibido */}
      <div style={{ ...warningStyles.box, ...warningStyles.info }}>
        <AiOutlineClockCircle size={20} style={warningStyles.icon} />
        <div>
          Pagamentos de boleto realizados <strong>até 13:59 em dias úteis</strong> são processados no mesmo dia.
          Pagamentos realizados <strong>após as 14:00</strong>, em <strong>finais de semana</strong> ou{' '}
          <strong>feriados</strong> serão processados no próximo dia útil.
        </div>
      </div>

      {/* Aviso direto se estiver fora do horário */}
      {!processingInfo.willProcessToday && (
        <div style={{ ...warningStyles.box, ...warningStyles.warn }}>
          <AiOutlineWarning size={20} style={warningStyles.icon} />
          <div>
            <strong>Atenção:</strong> {processingInfo.message} Se você confirmar agora, o pagamento
            ficará agendado para processamento no próximo dia útil.
          </div>
        </div>
      )}

      {/* Aviso crítico se boleto vence hoje e não vai processar hoje */}
      {dueDateRisk && (
        <div style={{ ...warningStyles.box, ...warningStyles.danger }}>
          <AiOutlineExclamationCircle size={20} style={warningStyles.icon} />
          <div>
            <strong>Risco de cancelamento:</strong> {dueDateRisk.message}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Componente de aviso pós-pagamento quando processamento será no próximo dia útil
 */
export const PostPaymentWarning = () => {
  const processingInfo = useMemo(() => getPaymentProcessingInfo(), []);

  if (processingInfo.willProcessToday) return null;

  return (
    <div style={{ ...warningStyles.box, ...warningStyles.warn, marginTop: '1rem' }}>
      <AiOutlineClockCircle size={20} style={warningStyles.icon} />
      <div>
        <strong>Pagamento agendado:</strong> Seu pagamento foi registrado, porém será processado
        apenas no <strong>próximo dia útil</strong> devido ao horário/dia da solicitação.
      </div>
    </div>
  );
};
